// src/Pages/Dashboard.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../Components/Layout/Layout';
import StatCard from '../Components/Dashboard/StatCard';
import MenuList from '../Components/Dashboard/MenuList';
import ActionButtons from '../Components/Dashboard/ActionButtons';
import { Plus } from "lucide-react";
import { Button } from "@/Components/ui/button";
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import MenuModal from '../Components/Dashboard/MenuModal';
import {
    UtensilsCrossed,
    SquareChartGantt,
    Eye,
    Layers3
} from "lucide-react";

const Dashboard = () => {
    const { t } = useTranslation();
    const [menus, setMenus] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [counters, setCounters] = useState({
        categories: 0,
        dishes: 0,
        products: 0
    });
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Get selected menu from localStorage on component mount
    useEffect(() => {
        const menuId = parseInt(localStorage.getItem('selectedMenu')) || 0;
        setSelectedMenu(menuId);
    }, []);

    // Fetch menus from API
    useEffect(() => {
        const fetchMenus = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/menu/GetMenus');
                setMenus(response.data);
                setError(null);
            } catch (error) {
                console.error('Error fetching menus:', error);
                setError(t('messages.errorFetchingMenus') || 'Fout bij het ophalen van menu\'s');
            } finally {
                setLoading(false);
            }
        };
        fetchMenus();
    }, [t]);

    // Handle menu selection
    const handleMenuSelect = async (menuId) => {
        setSelectedMenu(menuId);
        try {
            const response = await axiosInstance.get('/counter/GetCounters');
            setCounters(response.data);
        } catch (error) {
            console.error('Error updating counters:', error);
            setError(t('messages.errorUpdatingCounters') || 'Fout bij het bijwerken van tellers');
        }
    };

    // Handle saving new menu
    const handleSaveMenu = async (newMenu) => {
        try {
            const response = await axiosInstance.post('/menu/AddMenu', {
                id: 0,
                name: newMenu.name
            });
            
            if (response.data) {
                setMenus(prevMenus => [...prevMenus, response.data]);
                setError(null);
                
                // Show success message (you might want to add a toast notification here)
                console.log(t('messages.menuCreated') || 'Menu succesvol aangemaakt');
            }
        } catch (error) {
            console.error('Error saving menu:', error);
            setError(t('messages.errorSavingMenu') || 'Fout bij het opslaan van menu');
        }
    };

    // Fetch counters when selected menu changes
    useEffect(() => {
        const fetchCounters = async () => {
            if (selectedMenu === null) return;

            try {
                const response = await axiosInstance.get('/counter/GetCounters');
                setCounters(response.data);
                setError(null);
            } catch (error) {
                console.error('Error fetching counters:', error);
                setError(t('messages.errorFetchingCounters') || 'Fout bij het ophalen van tellers');
            }
        };
        fetchCounters();
    }, [selectedMenu, t]);

    // Stats configuration with translations
    const stats = [
        {
            title: t('dashboard.categories'),
            icon: <Layers3 size={45} />,
            count: counters.categories || 0,
            color: 'bg-ps-green',
            path: '/categories'
        },
        {
            title: t('dashboard.dishes'),
            icon: <UtensilsCrossed size={45} />,
            count: counters.dishes || 0,
            color: 'bg-ps-orange',
            path: '/dishes'
        },
        {
            title: t('dashboard.products'),
            icon: <SquareChartGantt size={45} />,
            count: counters.products || 0,
            color: 'bg-ps-pink',
            path: '/products'
        },
        {
            title: t('dashboard.viewResult'),
            icon: <Eye size={45} />,
            count: null,
            color: 'bg-ps-blue',
            path: '/result'
        }
    ];

    // Loading state
    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ps-blue-dark"></div>
                    <span className="ml-3 text-ps-blue-dark font-inter">
                        {t('common.loading')}
                    </span>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="relative">
                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
                        <div className="flex items-center">
                            <div className="flex-1">
                                <p className="font-inter text-sm">{error}</p>
                            </div>
                            <button 
                                onClick={() => setError(null)}
                                className="ml-4 text-red-400 hover:text-red-600"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 mt-0 md:mt-[40px]">
                    {stats.map((stat) => (
                        <StatCard 
                            key={stat.path} 
                            {...stat} 
                            navigate={navigate} 
                        />
                    ))}
                </div>

                {/* Menu List */}
                <MenuList
                    menus={menus}
                    selectedMenu={selectedMenu}
                    onMenuSelect={handleMenuSelect}
                    title={t('dashboard.menus')}
                />

                {/* Add Menu Button */}
                <div className="flex justify-end mt-4">
                    <Button
                        onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-green hover:bg-ps-green/90 text-white px-4 py-2 shadow-lg rounded-full"
                        disabled={loading}
                    >
                        <Plus className="h-6 w-6" />
                        <span>{t('dashboard.addMenu')}</span>
                    </Button>
                </div>

                {/* Menu Modal */}
                <MenuModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveMenu}
                />

                {/* Action Buttons */}
                <ActionButtons />
            </div>
        </Layout>
    );
};

export default Dashboard;